/**
 * Beleg per Mail verschicken — ein Formular für beide Wege.
 *
 * Vorher gab es zwei: der Editor (nach dem Festschreiben) fragte Empfänger,
 * Betreff und Nachricht ab, die Belegübersicht nur den Empfänger. Aus der
 * Übersicht ging deshalb ein anderer, kürzerer Text raus als aus dem Editor —
 * ohne dass man das vorher sah. Beide benutzen jetzt diese Komponente.
 *
 * Das Anschreiben kommt aus den gemeinsamen Vorlagen beider Inhaber
 * (`GET /mail/vorlagen`, seit Migration 164), gefüllt mit den Werten dieses Belegs. Was hier
 * geschrieben wird, lässt sich mit einem Klick als neue Vorlage übernehmen —
 * die konkreten Werte werden dabei wieder zu Platzhaltern.
 */
import { useEffect, useRef, useState } from "react";

import { ApiError, fehlerText } from "@vimaana/ui";

import type { AnredeKunde } from "../../lib/anrede";
import { istAngebotsbestaetigung, type InvoiceRegisterItem } from "../../lib/invoices";
import { parseKontakte } from "../../lib/kundeAnlage";
import MailvorlagenVerwaltung from "./MailvorlagenVerwaltung";
import {
  alsVorlage,
  BESTAETIGUNG_ANSCHREIBEN,
  erkanntePlatzhalter,
  vorlageFuellen,
  versandWerte,
} from "../../lib/mailVorlage";
import {
  useBelegVersenden,
  useMailKonto,
  useMailVorlageSpeichern,
  useMailVorlagen,
  type MailVorlageArt,
} from "../../hooks/useMail";
import { useMailSignatur } from "../../hooks/usePost";
import "../../styles/versand.css";

/* Dieselbe grobe Prüfung wie im Webhook (`_empfaenger_liste`): der Mailserver
   entscheidet, ob es die Adresse gibt — hier geht es nur darum, dass nichts
   Unzustellbares abgeschickt wird. Strenger als beides zusammen wäre eine
   Regel, die echte Adressen ablehnt. */
const MAIL_MUSTER = /^[^@\s,;<>]+@[^@\s,;<>]+\.[A-Za-z]{2,}$/;
const istMail = (adresse: string) => MAIL_MUSTER.test(adresse);

/** Getippten Text in Einzeladressen zerlegen — Komma und Semikolon trennen. */
function adressenAus(text: string): string[] {
  return (text || "")
    .split(/[,;]/)
    .map((teil) => teil.trim().replace(/^<|>$/g, "").trim())
    .filter(Boolean);
}

type Vorschlag = { email: string; wer: string };

/**
 * Was das System an Adressen zu diesem Kunden weiß: seine eigene und die
 * seiner Ansprechpartner aus `kontakte_json`. Die Ansprechpartner standen
 * bisher nur in der Kundenmaske und waren beim Versand nicht erreichbar —
 * wer an die Bauleitung schicken wollte, musste die Adresse abtippen.
 */
function mailVorschlaege(kundeEmail: string, kontakteJson: string, kundeName?: string): Vorschlag[] {
  const raus: Vorschlag[] = [];
  const gesehen = new Set<string>();
  const dazu = (email: string, wer: string) => {
    const adresse = (email || "").trim();
    if (!adresse || !istMail(adresse) || gesehen.has(adresse.toLowerCase())) return;
    gesehen.add(adresse.toLowerCase());
    raus.push({ email: adresse, wer });
  };
  dazu(kundeEmail, kundeName || "Kunde");
  for (const k of parseKontakte(kontakteJson)) {
    // Ein Kontaktweg gehört dem Kunden selbst, eine Person ist ein Ansprechpartner.
    const wer = k.art === "kontakt" ? [kundeName || "Kunde", k.role] : [k.name, k.role];
    dazu(k.email, wer.filter(Boolean).join(" · ") || "Ansprechpartner");
  }
  return raus;
}

type Props = {
  item: InvoiceRegisterItem;
  /** Aus dem Kundenstamm; im Formular überschreibbar. */
  kundeEmail: string;
  /** `kunde.kontakte_json` — liefert die Ansprechpartner als Vorschläge. */
  kundeKontakteJson?: string;
  kundeName?: string;
  /** Der Kundenstamm für die Briefanrede ({anrede}) — Anrede, Name, Typ. */
  kunde?: AnredeKunde;
  /** Beschriftung des Knopfes, der das Formular öffnet. */
  anstoss?: string;
};

export function BelegVersand({ item, kundeEmail, kundeKontakteJson = "", kundeName, kunde, anstoss }: Props) {
  const konto = useMailKonto();
  const vorlagen = useMailVorlagen();
  const senden = useBelegVersenden();
  const merken = useMailVorlageSpeichern();
  /** Die ganze Vorlagenverwaltung — umbenennen, löschen, Vorgabe wählen. */
  const [vorlagenOffen, setVorlagenOffen] = useState(false);

  const [offen, setOffen] = useState(false);

  /* Empfänger als Liste statt als ein Textfeld: mehrere Adressen sind der
     Normalfall (Kunde + Ansprechpartner), und als Chips sieht man auf einen
     Blick, wer die Mail bekommt. `entwurf` ist, was gerade getippt wird. */
  const [empfaenger, setEmpfaenger] = useState<string[]>(() => (kundeEmail ? [kundeEmail] : []));
  const [entwurf, setEntwurf] = useState("");
  const eingabe = useRef<HTMLInputElement>(null);
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  /** Sobald von Hand geändert wurde, überschreibt die Vorlage nichts mehr. */
  const [beruehrt, setBeruehrt] = useState(false);
  const [gesendetAn, setGesendetAn] = useState("");
  /** Kopie an den Partner oder frei getippte Adressen — zusätzlich zur eigenen Kopie. */
  const [cc, setCc] = useState("");
  const sig = useMailSignatur();
  const [gemerkt, setGemerkt] = useState("");

  const art: MailVorlageArt = item.kind === "quote" ? "quote" : "invoice";
  const wort = art === "quote" ? "Angebot" : "Rechnung";
  // Bei Firmen hängt die Anrede am Empfänger: geht die Mail an einen
  // Ansprechpartner, wird er angesprochen, sonst „Damen und Herren".
  const werte = versandWerte(item, kundeName, kunde, empfaenger[0]);
  // Angebotsbestätigung: festes Anschreiben, keine Vorlagenwahl (siehe
  // BESTAETIGUNG_ANSCHREIBEN) — die Angebotsvorlagen passen nicht dazu.
  const istBestaetigung = istAngebotsbestaetigung(item.kind, item.source_quote_nummer);
  const gemerkteVorlage = vorlagen.data?.vorlagen?.[art];
  const vorlage = istBestaetigung ? BESTAETIGUNG_ANSCHREIBEN : gemerkteVorlage;
  const auswahl = istBestaetigung ? [] : vorlagen.data?.listen?.[art] || [];

  // Beleg gewechselt: alles zurück auf Anfang.
  useEffect(() => {
    setEmpfaenger(kundeEmail ? [kundeEmail] : []);
    setEntwurf("");
    setBeruehrt(false);
    setGesendetAn("");
    setGemerkt("");
    setOffen(false);
  }, [item.id, kundeEmail]);

  // Vorbelegen, solange niemand getippt hat — auch wenn die Vorlage erst nach
  // dem Öffnen eintrifft.
  useEffect(() => {
    if (beruehrt || !vorlage) return;
    setBetreff(vorlageFuellen(vorlage.betreff, werte));
    setText(vorlageFuellen(vorlage.text, werte));
    // werte hängt an item; das deckt der item.id-Wechsel oben mit ab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorlage, item.id, beruehrt]);

  // Wechselt der Empfänger (und damit bei Firmen die Anrede), die erste Zeile
  // mitziehen — auch wenn schon getippt wurde, solange sie noch die alte
  // Anrede trägt. Den Rest des Textes fasst das nicht an.
  const letzteAnrede = useRef(werte.anrede);
  useEffect(() => {
    const alt = letzteAnrede.current;
    letzteAnrede.current = werte.anrede;
    if (alt === werte.anrede) return;
    setText((t) => (t.startsWith(alt) ? werte.anrede + t.slice(alt.length) : t));
  }, [werte.anrede]);

  if (konto.data && !konto.data.verbunden) {
    return (
      <p className="vg-versand-hinweis">
        {konto.data.hinweis
          ? konto.data.hinweis
          : "Dafür muss zuerst dein eigenes Postfach verbunden sein — das geht in den Einstellungen."}{" "}
        Danach geht {art === "quote" ? "das Angebot" : "die Rechnung"} unter deiner Adresse raus.{" "}
        <a href="/einstellungen">Zu den Einstellungen</a>
      </p>
    );
  }

  if (gesendetAn) {
    return (
      <p className="vg-versand-ok">
        ✓ An <strong>{gesendetAn}</strong> verschickt
        {konto.data?.email ? ` — Kopie in ${konto.data.email}` : ""}.
      </p>
    );
  }

  if (!offen) {
    return (
      <div className="vg-versand-anstoss">
        <button type="button" className="invoice-button" onClick={() => setOffen(true)}>
          {anstoss || `✉ ${wort} an den Kunden schicken`}
        </button>
        {!kundeEmail && <small>Beim Kunden ist keine E-Mail hinterlegt — Adresse im nächsten Schritt eintragen.</small>}
      </div>
    );
  }

  const hinzufuegen = (roh: string) => {
    const neue = adressenAus(roh);
    if (!neue.length) return;
    setEmpfaenger((bisher) => {
      const gesehen = new Set(bisher.map((a) => a.toLowerCase()));
      const dazu = neue.filter((a) => !gesehen.has(a.toLowerCase()));
      return dazu.length ? [...bisher, ...dazu] : bisher;
    });
    setEntwurf("");
  };

  const entfernen = (adresse: string) =>
    setEmpfaenger((bisher) => bisher.filter((a) => a !== adresse));

  const beiTaste = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === " ") {
      if (!entwurf.trim()) return;
      e.preventDefault();
      hinzufuegen(entwurf);
      return;
    }
    /* Rücktaste im leeren Feld holt den letzten Chip zum Korrigieren zurück,
       statt ihn wortlos zu löschen. */
    if (e.key === "Backspace" && !entwurf && empfaenger.length) {
      e.preventDefault();
      setEntwurf(empfaenger[empfaenger.length - 1]);
      setEmpfaenger((bisher) => bisher.slice(0, -1));
    }
  };

  /* Was gerade getippt, aber noch nicht als Chip übernommen wurde, zählt beim
     Senden trotzdem mit — sonst verschwände eine eben eingegebene Adresse. */
  const alleAdressen = [...empfaenger, ...adressenAus(entwurf)];
  const bereit = alleAdressen.length > 0 && alleAdressen.every(istMail);
  const offeneVorschlaege = mailVorschlaege(kundeEmail, kundeKontakteJson, kundeName).filter(
    (v) => !alleAdressen.some((a) => a.toLowerCase() === v.email.toLowerCase()),
  );

  const merkenJetzt = () => {
    const vorlageText = alsVorlage(text, werte);
    merken.mutate(
      { kind: art, betreff: alsVorlage(betreff, werte), text: vorlageText },
      {
        onSuccess: () => {
          const gefunden = erkanntePlatzhalter(vorlageText);
          setGemerkt(
            gefunden.length
              ? `Gemerkt. Beim nächsten ${wort} steht dieser Text schon da; ${gefunden
                  .map((p) => `{${p}}`)
                  .join(", ")} werden dann neu eingesetzt.`
              : `Gemerkt. Beim nächsten ${wort} steht dieser Text schon da.`,
          );
        },
      },
    );
  };

  return (
    <div className="vg-versand">
      <div className="vg-versand-feld">
        <span id="vg-versand-an-label">An *</span>
        {/* Kein <label>: der Klick auf einen Chip-Knopf darf nicht im Feld
            landen. Stattdessen fokussiert der Rahmen selbst die Eingabe. */}
        <div className="vg-versand-an" onClick={() => eingabe.current?.focus()}>
          {empfaenger.map((adresse) => (
            <span
              key={adresse}
              className={`vg-versand-chip${istMail(adresse) ? "" : " fehler"}`}
              title={istMail(adresse) ? adresse : `${adresse} — sieht nicht wie eine E-Mail-Adresse aus`}
            >
              {adresse}
              <button type="button" onClick={() => entfernen(adresse)} aria-label={`${adresse} entfernen`}>×</button>
            </span>
          ))}
          <input
            ref={eingabe}
            type="text"
            inputMode="email"
            autoComplete="off"
            aria-labelledby="vg-versand-an-label"
            value={entwurf}
            onChange={(e) => setEntwurf(e.target.value)}
            onKeyDown={beiTaste}
            onBlur={() => hinzufuegen(entwurf)}
            placeholder={empfaenger.length ? "weitere Adresse …" : "kunde@example.de"}
          />
        </div>
        {offeneVorschlaege.length > 0 && (
          <div className="vg-versand-vorschlaege">
            <span>Aus dem Kundenstamm</span>
            {offeneVorschlaege.map((v) => (
              <button
                key={v.email}
                type="button"
                className="vg-versand-vorschlag"
                onClick={() => hinzufuegen(v.email)}
              >
                + {v.email}<small>{v.wer}</small>
              </button>
            ))}
          </div>
        )}
      </div>
      <label className="vg-versand-feld">
        <span>Betreff</span>
        <input
          value={betreff}
          onChange={(e) => { setBetreff(e.target.value); setBeruehrt(true); }}
        />
      </label>
      <label className="vg-versand-feld">
        <span>Nachricht</span>
        <textarea
          rows={9}
          value={text}
          onChange={(e) => { setText(e.target.value); setBeruehrt(true); }}
        />
      </label>

      {/* Mehr als eine Vorlage? Dann zur Auswahl stellen — sonst wäre das
          Anlegen einer zweiten folgenlos. Eingefügt wird mit den Werten dieses
          Belegs gefüllt, wie beim Vorbelegen. */}
      {auswahl.length > 1 && (
        <div className="vg-versand-auswahl">
          {auswahl.map((eintrag) => (
            <button
              type="button"
              key={eintrag.id}
              className={"vg-versand-chip" + (eintrag.ist_standard ? " ist-standard" : "")}
              onClick={() => {
                setBetreff(vorlageFuellen(eintrag.betreff, werte));
                setText(vorlageFuellen(eintrag.text, werte));
                setBeruehrt(true);
                setGemerkt(`Vorlage „${eintrag.name}" eingefügt.`);
              }}
              title={eintrag.ist_standard ? "Vorgabe" : "Diese Vorlage einfügen"}
            >
              {eintrag.ist_standard ? "★ " : ""}{eintrag.name}
            </button>
          ))}
        </div>
      )}

      {!istBestaetigung && <div className="vg-versand-vorlage">
        <button
          type="button"
          className="vg-versand-link"
          onClick={merkenJetzt}
          disabled={merken.isPending || !text.trim()}
        >
          {merken.isPending ? "Merkt …" : "Als Vorlage übernehmen"}
        </button>
        <button
          type="button"
          className="vg-versand-link"
          onClick={() => setVorlagenOffen(true)}
          title="Vorlagen anlegen, umbenennen, löschen und die Vorgabe wählen"
        >
          Vorlagen verwalten
        </button>
        <small>
          {gemerkteVorlage?.eigen ? `Vorgabe: „${gemerkteVorlage.name}"` : "Vorgabetext"} für {wort === "Angebot" ? "Angebote" : "Rechnungen"}.
          Übernehmen schreibt in die Vorgabe.
        </small>
      </div>}

      {vorlagenOffen && (
        <MailvorlagenVerwaltung startArt={art} onClose={() => setVorlagenOffen(false)} />
      )}
      {gemerkt && <p className="vg-versand-gemerkt">{gemerkt}</p>}
      {merken.isError && (
        <p className="vg-versand-fehler">
          {fehlerText(merken.error, "Die Vorlage ließ sich nicht merken.")}
        </p>
      )}
      {/* Bewusst `fehlerText` statt `error.message`: letzteres ist die technische
          Zeile („HTTP 428 on POST /api/…") und sagt niemandem, was zu tun ist —
          der verständliche Satz steht im `detail` der Serverantwort. Bei 428
          fehlt die Postfachverbindung, dann gehört der Weg dorthin dazu. */}
      {senden.isError && (
        <p className="vg-versand-fehler">
          {fehlerText(senden.error, "Der Versand ist fehlgeschlagen.")}{" "}
          {senden.error instanceof ApiError && senden.error.status === 428 && (
            <a href="/einstellungen">Postfach neu verbinden</a>
          )}
        </p>
      )}

      <label className="vg-versand-cc">
        <span>Kopie an</span>
        <input
          type="text" inputMode="email" autoComplete="off" value={cc} onChange={(e) => setCc(e.target.value)}
          placeholder="optional — mehrere mit Komma"
        />
        {(sig.data?.partner || []).filter((p) => !cc.toLowerCase().includes(p.email.toLowerCase())).map((p) => (
          <button key={p.email} type="button" className="vg-versand-cc-knopf" title={p.email}
            onClick={() => setCc((v) => (v.trim() ? `${v.trim().replace(/[,;]$/, "")}, ${p.email}` : p.email))}>
            + {p.name.split(" ")[0] || p.email}
          </button>
        ))}
      </label>
      {sig.data?.signatur && <pre className="vg-versand-signatur" aria-label="Signatur, wird angehängt">{sig.data.signatur}</pre>}
      <small className="vg-versand-fuss">
        Das PDF hängt automatisch an. Absender ist {konto.data?.email || "dein verbundenes Postfach"};
        eine Kopie geht an dich selbst.
      </small>
      <div className="vg-versand-aktionen">
        <button
          type="button"
          className="invoice-button"
          onClick={() => setOffen(false)}
          disabled={senden.isPending}
        >
          Abbrechen
        </button>
        <button
          type="button"
          className="invoice-button primary"
          disabled={!bereit || senden.isPending}
          onClick={() =>
            senden.mutate(
              { invoiceId: item.id, an: alleAdressen.join(", "), betreff, text, cc },
              {
                onSuccess: (d) => {
                  setGesendetAn(d.an || alleAdressen.join(", "));
                  setOffen(false);
                },
              },
            )
          }
        >
          {senden.isPending ? "Sendet …" : "Jetzt senden"}
        </button>
      </div>
    </div>
  );
}
